import { useMemo } from 'react';

import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';

import { PendingItemStatus, PendingItemType } from '@appTypes/enums';
import { programSel } from '@context/duck/selectorsTyped';
import { useCampImportStatus } from '@modules/advancement/advancementImport/hooks';
import {
  useGetActivitiesQuery,
  useGetAdvancementHistoryQuery,
  useGetAdvancementsReadyAwardQuery,
} from '@modules/rtk/esb-api';
import tagFactory from '@modules/rtk/utils/tagFactory';
import { Program } from '@modules/shared/constants';
import { formatToAPIDate } from '@modules/utils/datesLuxon';
import { SBL_6013_importCouncilFileSel } from '@shared/duck/selectors/flags.selectors';
import {
  organizationGuidSel,
  packRosterItemsSel,
} from '@shared/duck/selectors/general.selectors';

import { transformActivities } from '../advancementHistory/activityUtils';
import { activitiesPayloadSel } from '../advancementHistory/duck/selectorsTyped';
import {
  type AdvancementImportDto,
  mapAdvancementHistoryDtoToModel,
  mapAdvancementImportDtoToModel,
  mapCampImportToHistoryRow,
} from '../advancementHistory/utils';
import { adultItemsSel } from '../common';
import {
  useGetBulkAdvancementHistoryQuery,
  useGetUnitAdvancementImportsQuery,
} from './api';

/**
 * To be used by tabs on Pending Items to fetch Ranks, Adventures, etc.
 */
export function useHistoryItemsQuery(props: {
  advancementType: PendingItemType;
}) {
  const selectedOrgGuid = useSelector(organizationGuidSel);
  const rosterItemsCount = useSelector(packRosterItemsSel);

  const perPage = rosterItemsCount?.length || 100;
  const response = useGetAdvancementHistoryQuery(
    {
      body: {
        advancementType: props.advancementType,
        status: [
          PendingItemStatus.COMPLETED,
          PendingItemStatus.COUNSELOR_APPROVED,
        ],
        showAdults: true,
        showYouths: true,
        organizationGuid: selectedOrgGuid,
      },
      params: {
        perPage,
      },
    },
    {
      skip:
        !selectedOrgGuid ||
        !rosterItemsCount ||
        !props.advancementType ||
        props.advancementType === PendingItemType.ACTIVITIES,
    },
  );

  const result = useMemo(() => {
    const data = mapAdvancementHistoryDtoToModel(response?.data || []);
    return {
      ...response,
      data,
    };
  }, [response]);

  return result;
}

/**
 * Used on Pending Items - Requirements section
 */
export function usePendingItemsRequirementsQuery() {
  const selectedOrgGuid = useSelector(organizationGuidSel);
  const rosterItemsCount = useSelector(packRosterItemsSel);
  const program = useSelector(programSel);

  const perPage = rosterItemsCount?.length || 100;

  const skip = !selectedOrgGuid || !rosterItemsCount?.length;

  const advancementTypes = [
    PendingItemType.RANKS,
    PendingItemType.AWARDS,
    PendingItemType.ADVENTURES,
    program !== Program.CUB_SCOUT && PendingItemType.MERIT_BADGES,
  ].filter(Boolean) as PendingItemType[];

  const response = useGetBulkAdvancementHistoryQuery(
    {
      advancementTypes,
      payload: {
        body: {
          status: [PendingItemStatus.STARTED, PendingItemStatus.COMPLETED],
          showAdults: true,
          showYouths: true,
          organizationGuid: selectedOrgGuid,
        },
        params: {
          perPage,
        },
      },
      extraTags: [
        tagFactory.AdvancementHistory.toApproveTabRequirements(selectedOrgGuid),
      ],
    },
    { skip },
  );

  return response;
}

export function usePendingActivitiesQuery(props: { skip: boolean }) {
  const selectedOrgGuid = useSelector(organizationGuidSel);
  const rosterAdults = useSelector(adultItemsSel);
  const activitiesResponse = useGetActivitiesQuery(
    {
      params: {
        page: 1,
        perPage: 300,
      },
      body: {
        hostOrganizationGuid: selectedOrgGuid,
        includeActivities: 'both',
        endDate: formatToAPIDate(DateTime.now().endOf('day')),
        startDate: formatToAPIDate(
          DateTime.now().endOf('day').minus({ year: 1 }),
        ),
      },
    },
    {
      skip: !selectedOrgGuid || props.skip,
    },
  );

  const activities = useMemo(
    () =>
      transformActivities(
        activitiesResponse.data?.activities || [],
        rosterAdults,
      ),
    [activitiesResponse.data, rosterAdults],
  );

  return { activities, isLoading: activitiesResponse.isLoading };
}

/**
 * Used on Roster - History tab
 */
export function useUnitHistoryItemsQuery() {
  const selectedOrgGuid = useSelector(organizationGuidSel);
  const rosterItemsCount = useSelector(packRosterItemsSel);

  const perPage = rosterItemsCount?.length || 100;

  const skip = !selectedOrgGuid || !rosterItemsCount?.length;
  const payload = useSelector(activitiesPayloadSel);
  const rosterAdults = useSelector(adultItemsSel);

  const unitImportsResponse = useGetUnitAdvancementImportsQuery(
    {
      params: { organizationGuid: selectedOrgGuid },
    },
    {
      skip: !selectedOrgGuid,
    },
  );

  const campImportEnabled = useSelector(SBL_6013_importCouncilFileSel);
  const { status: campImportStatus } = useCampImportStatus({
    enabled: campImportEnabled,
  });

  const activitiesResponse = useGetActivitiesQuery(
    {
      params: {
        page: 1,
        perPage: 1000,
      },
      body: {
        includeActivities: 'both',
        endDate: payload.endDate,
        startDate: payload.startDate,
        hostOrganizationGuid: payload.organizationGuid,
      },
    },
    {
      skip: !payload.organizationGuid,
    },
  );

  const unitImports = useMemo(() => {
    const data: AdvancementImportDto[] = mapAdvancementImportDtoToModel(
      unitImportsResponse.data || [],
      rosterAdults,
    );
    return data;
  }, [unitImportsResponse.data, rosterAdults]);

  const campImports = useMemo(
    () => mapCampImportToHistoryRow(campImportStatus, rosterAdults),
    [campImportStatus, rosterAdults],
  );

  const activities = useMemo(
    () =>
      transformActivities(
        activitiesResponse.data?.activities || [],
        rosterAdults,
      ),
    [activitiesResponse.data, rosterAdults],
  );

  const response = useGetBulkAdvancementHistoryQuery(
    {
      advancementTypes: [
        PendingItemType.RANKS,
        PendingItemType.AWARDS,
        PendingItemType.ADVENTURES,
        PendingItemType.MERIT_BADGES,
      ],
      payload: {
        body: {
          status: [
            PendingItemStatus.STARTED,
            PendingItemStatus.LEADER_SIGNED,
            PendingItemStatus.COUNSELOR_APPROVED,
            PendingItemStatus.LEADER_APPROVED,
            PendingItemStatus.AWARDED,
          ],
          showAdults: true,
          showYouths: true,
          organizationGuid: selectedOrgGuid,
        },
        params: {
          perPage,
        },
      },
    },
    { skip },
  );

  const result = useMemo(() => {
    const data = mapAdvancementHistoryDtoToModel(response?.data || []);
    return {
      ...response,
      data,
    };
  }, [response]);

  return {
    advancementsResponse: result,
    activities,
    unitImports,
    campImports,
    isLoading: activitiesResponse.isFetching || result.isFetching,
    isError: activitiesResponse.isError || result.isError,
  };
}

export function useAdvancementsToAwardReport(params: { skip?: boolean }) {
  const selectedOrgGuid = useSelector(organizationGuidSel);
  const response = useGetAdvancementsReadyAwardQuery(
    {
      organizationGuid: selectedOrgGuid,
      body: [],
      params: {
        orderBy: 'advancement',
        advancementType: 'all',
      },
    },
    {
      skip: params.skip || !selectedOrgGuid,
    },
  );
  return response;
}
